/**
 * Assignment Status Constants
 *
 * Centralized definitions for all cast assignment statuses
 * to ensure consistency across the application
 *
 * Simplified workflow (January 2025):
 * - Admin creates: 'offered' (direct assignment)
 * - Dancer responds: 'accepted' or 'declined'
 * - For standby dancers: use B Cast instead of separate status
 * - Admin removes: 'removed' (remove_cast_assignment; the row and its fees
 *   are kept, the dancer is no longer cast). Set only by that function.
 */

/**
 * All possible assignment statuses
 */
export const ASSIGNMENT_STATUSES = {
  // Pending status (requires dancer response)
  OFFERED: 'offered', // Direct offer (also covers legacy 'pending', 'assigned', 'invited')

  // Response statuses
  ACCEPTED: 'accepted', // Dancer accepted (final positive state)
  DECLINED: 'declined', // Dancer declined

  // Terminal status set by remove_cast_assignment (2026-09-23)
  REMOVED: 'removed', // Removed from the cast; row kept for its fees
} as const;

/**
 * Assignment status groups for categorization
 */
export const ASSIGNMENT_STATUS_GROUPS = {
  // Statuses requiring dancer response
  PENDING: [ASSIGNMENT_STATUSES.OFFERED] as const,

  // Accepted statuses
  ACCEPTED: [ASSIGNMENT_STATUSES.ACCEPTED] as const,

  // Declined statuses
  DECLINED: [ASSIGNMENT_STATUSES.DECLINED] as const,

  // Removed from the cast by an admin
  REMOVED: [ASSIGNMENT_STATUSES.REMOVED] as const,
} as const;

/**
 * Statuses that do not count as cast: nobody is paid, billed or shown as cast
 * for a row in one of these. Every other status, and a NULL one, counts.
 * Mirrors `countsAsCast` (apps/web/app/admin/events/_lib/server/cast-participation.ts)
 * and the SQL guards `soft_delete_event_from_airtable_guarded` /
 * `update_event_from_airtable_guarded`.
 */
export const NOT_CAST_ASSIGNMENT_STATUSES = [
  ...ASSIGNMENT_STATUS_GROUPS.DECLINED,
  ...ASSIGNMENT_STATUS_GROUPS.REMOVED,
] as const;

/**
 * PostgREST `or` filter keeping the rows that count as cast:
 * `.or(CAST_STATUS_OR_FILTER)`, or on an embedded resource
 * `.or(CAST_STATUS_OR_FILTER, { referencedTable: 'cast_assignments' })`.
 *
 * Why not `.not('assignment_status', 'in', ...)`: SQL `NOT (x IN (...))` is
 * NULL for a NULL status, so that form silently drops NULL-status rows, which
 * count as cast. The column is nullable (default 'offered').
 */
export const CAST_STATUS_OR_FILTER = `assignment_status.is.null,assignment_status.not.in.(${NOT_CAST_ASSIGNMENT_STATUSES.join(',')})`;

/**
 * Type for assignment status values
 */
export type AssignmentStatus =
  (typeof ASSIGNMENT_STATUSES)[keyof typeof ASSIGNMENT_STATUSES];

/**
 * Type for assignment status group keys
 */
export type AssignmentStatusGroup = keyof typeof ASSIGNMENT_STATUS_GROUPS;

/**
 * PostgREST `or` filter keeping every row except removed ones. For surfaces
 * that list declined assignments on purpose (an admin cast grid, a dancer's
 * own history) but must never show a removed dancer as cast. NULL-safe for the
 * same reason as `CAST_STATUS_OR_FILTER`.
 */
export const NOT_REMOVED_STATUS_OR_FILTER = `assignment_status.is.null,assignment_status.neq.${ASSIGNMENT_STATUSES.REMOVED}`;
